import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  ArrowRight,
  Check,
  Phone,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  agencyOwnerNeedsEmailVerification,
  getApiUrl,
  getSignupErrorMessage,
  normalizeLoginEmail,
  getDashboardForUser,
  AGENCY_DASHBOARD_PATH,
  persistAuthSession,
} from '../../utils/authUtils';

const API = getApiUrl();

const AgencySignup = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuth();

  useEffect(() => {
    if (!authLoading && user) {
      if (agencyOwnerNeedsEmailVerification(user)) {
        navigate('/verify-email', {
          replace: true,
          state: {
            email: user.email,
            role: 'agency_owner',
            redirectTo: AGENCY_DASHBOARD_PATH,
            agencyId: user.agency_id,
          },
        });
        return;
      }
      navigate(getDashboardForUser(user), { replace: true });
    }
  }, [authLoading, user, navigate]);

  const [formData, setFormData] = useState({
    agency_name: '',
    owner_name: '',
    owner_email: '',
    phone: '',
    password: '',
    confirm_password: '',
    termsAccepted: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (formError) setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (
      !formData.agency_name ||
      !formData.owner_name ||
      !formData.owner_email ||
      !formData.password ||
      !formData.phone
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: normalizeLoginEmail(formData.owner_email),
        password: formData.password,
        name: formData.owner_name.trim(),
        agency_name: formData.agency_name.trim(),
        first_name: formData.owner_name.trim().split(' ')[0] || undefined,
      };

      const response = await axios.post(`${API}/auth/signup/agency`, payload, {
        withCredentials: true,
      });

      const { token, user: userData, message, agency_id: agencyId } = response.data;
      if (token) {
        persistAuthSession({ token, user: userData });
        await checkAuth();
      }

      const successMessage =
        message || 'Agency created. Please check your email for your verification code.';
      toast.success(successMessage);

      const signedInUser = userData || {};
      if (agencyOwnerNeedsEmailVerification(signedInUser)) {
        navigate('/verify-email', {
          replace: true,
          state: {
            email: signedInUser.email || payload.email,
            role: 'agency_owner',
            redirectTo: AGENCY_DASHBOARD_PATH,
            agencyId: signedInUser.agency_id || agencyId,
          },
        });
        return;
      }

      if (signedInUser.agency_id || agencyId) {
        navigate(getDashboardForUser(signedInUser), { replace: true });
        return;
      }

      navigate('/login', { replace: true });
    } catch (error) {
      const message = getSignupErrorMessage(error);
      setFormError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Agency workspace for credit repair teams',
    'Client management and dispute workflows',
    'Automated agency owner email verification',
    'Secure portal access for your clients',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex" data-testid="agency-signup-page">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Building2 size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">FundElite AI</h1>
              <p className="text-sm text-white/70">Agency Platform</p>
            </div>
          </div>
        </div>

        <div className="relative">
          <h2 className="text-4xl font-bold text-white leading-tight mb-6">
            Launch Your <br />
            Credit Repair Agency
          </h2>
          <p className="text-lg text-white/80 max-w-md mb-8">
            Create your agency account, verify your email, and start onboarding clients on FundElite.
          </p>

          <div className="space-y-4">
            {features.map((feature) => (
              <div key={feature} className="flex items-center gap-3 text-white/90">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check size={14} />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-white/60 text-sm">
          © 2026 FundElite AI. All rights reserved.
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Building2 size={22} className="text-white" />
            </div>
            <span className="text-xl font-bold text-stone-900">FundElite AI</span>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-stone-900">Create your agency</h2>
            <p className="text-slate-500 mt-2">Register as an agency owner on FundElite</p>
          </div>

          {formError ? (
            <div
              className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="signup-form-error"
              role="alert"
            >
              {formError}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="agency_name" className="text-slate-700">Agency Name *</Label>
              <IconInput
                id="agency_name"
                name="agency_name"
                type="text"
                icon={Building2}
                containerClassName="mt-1.5"
                placeholder="Acme Credit Repair"
                value={formData.agency_name}
                onChange={handleChange}
                data-testid="agency-signup-agency-name"
              />
            </div>

            <div>
              <Label htmlFor="owner_name" className="text-slate-700">Owner Name *</Label>
              <IconInput
                id="owner_name"
                name="owner_name"
                type="text"
                icon={User}
                containerClassName="mt-1.5"
                placeholder="Jane Smith"
                value={formData.owner_name}
                onChange={handleChange}
                data-testid="agency-signup-owner-name"
              />
            </div>

            <div>
              <Label htmlFor="owner_email" className="text-slate-700">Owner Email *</Label>
              <IconInput
                id="owner_email"
                name="owner_email"
                type="email"
                icon={Mail}
                containerClassName="mt-1.5"
                placeholder="owner@agency.com"
                value={formData.owner_email}
                onChange={handleChange}
                data-testid="agency-signup-owner-email"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-700">Phone Number *</Label>
              <IconInput
                id="phone"
                name="phone"
                type="tel"
                icon={Phone}
                containerClassName="mt-1.5"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={handleChange}
                data-testid="agency-signup-phone"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password *</Label>
              <IconInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                data-testid="agency-signup-password"
                trailing={(
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 z-[1] flex w-11 items-center justify-center text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="text-slate-700">Confirm Password *</Label>
              <IconInput
                id="confirm_password"
                name="confirm_password"
                type="password"
                icon={Lock}
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={formData.confirm_password}
                onChange={handleChange}
                data-testid="agency-signup-confirm-password"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="agency-signup-terms"
              />
              <span className="text-sm text-slate-600 leading-relaxed">
                I agree to the{' '}
                <a href="/terms" className="text-emerald-600 hover:text-emerald-700 underline" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-emerald-600 hover:text-emerald-700 underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </span>
            </label>

            <Button
              type="submit"
              className="w-full btn-primary py-3 mt-4"
              disabled={loading}
              data-testid="agency-signup-submit"
            >
              {loading ? 'Creating agency...' : 'Create Agency Account'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          <p className="text-center mt-4 text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgencySignup;
